import React, { useState, useEffect, useMemo, useReducer } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  DollarSign, CheckCircle2, Clock, XCircle, Calendar, CreditCard, AlertCircle,
  Search, Filter, Download, Eye, ArrowUpRight, ArrowDownRight, Users, Building2,
  RefreshCw, Settings, ChevronRight, ChevronDown, FileSpreadsheet, FileText,
  Info, Layers, Truck, Box, Sparkles, Package, MoreHorizontal, CheckSquare, Wallet,
  AlertTriangle,
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import AdminWalletsTab from '../components/admin/AdminWalletsTab';
import TransferHoldControls from '../components/admin/TransferHoldControls';

// ═══════════════════════════════════════════════════════════════════════════
// iter-230ax — MOCK DATA for Phase B UI review (per user request 2026-06-01)
// Backend wiring lands in Phase A immediately after the UI review is signed
// off. Everything tagged `MOCK_` below is throwaway data — final version
// reads from the iter-230ax backend endpoints (payout-settings, daily
// aggregates, batched transfers, breakdown). DO NOT ship to production
// without removing the MOCK_ tag.
// ═══════════════════════════════════════════════════════════════════════════

const PAYOUT_SETTINGS_ROWS = [
  {
    id: 'products_aniyor',
    icon: Package,
    label: 'Products — Aniyor Managed Delivery',
    description: 'Physical goods shipped via our integrated logistics network.',
    defaultHold: 7,
    defaultAnchor: 'delivery_date',
    anchorOptions: [
      { value: 'delivery_date', label: 'Delivery Date' },
      { value: 'start_date', label: 'Start Date' },
      { value: 'dispatch_date', label: 'Dispatch Date' },
      { value: 'order_date', label: 'Order Date' },
      { value: 'delivery_confirmation_date', label: 'Delivery Confirmation Date' },
    ],
  },
  {
    id: 'products_self',
    icon: Truck,
    label: 'Products — Self-Managed Delivery',
    description: 'Vendor handles their own logistics. Customer confirmation drives release.',
    defaultHold: 7,
    defaultAnchor: 'delivery_date',
    anchorOptions: [
      { value: 'delivery_date', label: 'Delivery Date' },
      { value: 'start_date', label: 'Start Date' },
      { value: 'dispatch_date', label: 'Dispatch Date' },
      { value: 'order_date', label: 'Order Date' },
      { value: 'delivery_confirmation_date', label: 'Delivery Confirmation Date' },
    ],
  },
  {
    id: 'services_rentals',
    icon: Sparkles,
    label: 'Services / Rentals / Subscriptions',
    description: 'Non-shipping verticals — service bookings, rental contracts, recurring subscriptions.',
    defaultHold: 5,
    defaultAnchor: 'start_date',
    anchorOptions: [
      { value: 'start_date', label: 'Start Date' },
      { value: 'order_booking_date', label: 'Order / Booking Date' },
      { value: 'service_completion_date', label: 'Service Completion Date' },
    ],
  },
];

// Customer Delivery Confirmation Fallback — static display per user decision
// (drop the 24/48/72 dropdown, keep 72 Hrs fixed).
const CONFIRMATION_FALLBACK_HOURS = 72;

const EXPORT_PERIODS = [
  { key: 'weekly_ledger', apiKey: 'weekly', label: 'Weekly Ledger', help: 'Last 7-day rolling window' },
  { key: 'monthly_book', apiKey: 'monthly', label: 'Monthly Book', help: 'Full calendar month' },
  { key: 'quarterly_report', apiKey: 'quarterly', label: 'Quarterly Report', help: 'Q1 / Q2 / Q3 / Q4' },
  { key: 'financial_year_suite', apiKey: 'fy', label: 'Financial Year Suite', help: 'Apr 1 → Mar 31 (IN FY)' },
];

// Reducer for the 4-row settings form. Single source of truth for the
// settings tab; once Phase A lands we initialise from the GET response
// and dispatch `INIT_FROM_SERVER` instead of using defaults.
function payoutSettingsReducer(state, action) {
  switch (action.type) {
    case 'SET_HOLD':
      return { ...state, [action.row]: { ...state[action.row], hold_days: action.value } };
    case 'SET_ANCHOR':
      return { ...state, [action.row]: { ...state[action.row], anchor_from: action.value } };
    case 'INIT_FROM_SERVER':
      return action.payload;
    case 'RESET_DEFAULTS': {
      const fresh = {};
      PAYOUT_SETTINGS_ROWS.forEach((r) => {
        fresh[r.id] = { hold_days: r.defaultHold, anchor_from: r.defaultAnchor };
      });
      return fresh;
    }
    default:
      return state;
  }
}

function initialPayoutSettings() {
  const init = {};
  PAYOUT_SETTINGS_ROWS.forEach((r) => {
    init[r.id] = { hold_days: r.defaultHold, anchor_from: r.defaultAnchor };
  });
  return init;
}

// ─── Export Report dropdown (iter-230ay — streams from backend) ─────────
// Period choices map to backend `/api/admin/payouts/export` endpoint.
// XLSX format includes `=SUM(...)` formulas at the bottom of numeric columns.
function ExportReportMenu({ apiCall, currentFilters }) {
  const { toast } = useToast();
  const handleExport = async (period, format) => {
    try {
      const params = new URLSearchParams({ period: period.apiKey, format });
      if (currentFilters?.status && currentFilters.status !== 'all') params.append('status', currentFilters.status);
      if (currentFilters?.search) params.append('search', currentFilters.search);
      const backend = process.env.REACT_APP_BACKEND_URL;
      // iter-245 — Auth token key fix. The app stores the JWT under
      // the key 'token' (see AuthContext.js:429) — NOT 'access_token'.
      // Pre-fix this handler read the wrong key → empty string →
      // backend returned 401 Unauthorized on every export attempt.
      // Production symptom: clicking any Export Report option showed
      // a "Could not generate export" toast with no further detail
      // (matched the 401 the server-side log was recording).
      const token = sessionStorage.getItem('token')
                 || localStorage.getItem('token')
                 || sessionStorage.getItem('access_token')   // legacy fallback
                 || localStorage.getItem('access_token')
                 || '';
      const resp = await fetch(`${backend}/api/admin/payouts/export?${params.toString()}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '');
      a.href = url;
      a.download = `aniyor-payouts-${period.apiKey}-${ts}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: 'Export ready', description: `${period.label} · ${format.toUpperCase()} downloaded.` });
    } catch (err) {
      toast({
        title: 'Export failed',
        description: err?.message || 'Could not generate export.',
        variant: 'destructive',
      });
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" data-testid="export-report-trigger" className="gap-2">
          <Download className="h-4 w-4" />
          Export Report
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Choose reporting period</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {EXPORT_PERIODS.map((p) => (
          <DropdownMenuSub key={p.key}>
            <DropdownMenuSubTrigger data-testid={`export-period-${p.key}`}>
              <div className="flex flex-col">
                <span className="font-medium">{p.label}</span>
                <span className="text-[10px] text-gray-500">{p.help}</span>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-56">
              <DropdownMenuLabel className="text-xs">Export format</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExport(p, 'xlsx')}
                data-testid={`export-${p.key}-xlsx`}>
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-700" />
                <div className="flex flex-col">
                  <span>XLSX (Formatted)</span>
                  <span className="text-[10px] text-gray-500">
                    With =SUM totals · branded header
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport(p, 'csv')}
                data-testid={`export-${p.key}-csv`}>
                <FileText className="h-4 w-4 mr-2 text-gray-700" />
                <div className="flex flex-col">
                  <span>CSV (Raw Data)</span>
                  <span className="text-[10px] text-gray-500">
                    10-line SSOT breakdown + Batch IDs
                  </span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Transaction Breakdown Drawer (iter-230ay — live 10-line SSOT) ─────
// Shows the Financial SSOT itemised split for one transaction. Reads
// the full `breakdown` blob returned by
// `/api/admin/payouts/transactions-detailed`.
function TransactionBreakdownDrawer({ txn }) {
  const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const b = txn.breakdown || {};
  const isSelfShipping = (b.shipping_mode || txn.shipping_mode) === 'self';
  // Phase 7 — Commission % read directly from stored commission_rate.
  // NEVER derive from commission_amount / gross. If stored as fraction (0.15),
  // multiply by 100; if already as pct (15.0), use as-is.
  const commRateRaw = b.commission_rate;
  const commRatePct = commRateRaw != null
    ? (parseFloat(commRateRaw) < 1.5 ? parseFloat(commRateRaw) * 100 : parseFloat(commRateRaw))
    : null;
  const commPctDisplay = commRatePct != null ? commRatePct.toFixed(1).replace(/\.0$/, '') : '—';

  // Commission source — never show "default", always translate to business label
  const commSource = b.commission_rate_source || b.commission_rate_source_v2;
  const commSourceLabel = (() => {
    if (!commSource) return 'Subscription plan';
    if (commSource.startsWith('override')) return 'Custom Override';
    if (commSource === 'MISSING_PLAN') return 'Missing Plan — admin action required';
    if (commSource === 'subscription_stale') return 'Subscription (cached value was stale; plan rate used)';
    if (commSource === 'subscription_history') return 'Subscription (plan active on order date)';
    if (commSource === 'default') return 'Missing Plan — no subscription found';
    return commSource.replace(/_/g, ' ');
  })();
  // PG total = PG fee + PG GST. Also map legacy 'gateway_fee' → pg_fee
  const pgFee = Number(b.pg_fee) || Number(b.gateway_fee) || 0;
  const pgGst = Number(b.pg_gst) || Number(b.gateway_gst) || 0;
  const pgTotal = pgFee + pgGst;
  // Also map legacy seller_pool aliases
  const sellerPool = Number(b.seller_pool) || Number(b.shipping_subsidy) || 0;
  // gross_total: prefer normalised v2 field, fallback to legacy 'gross' field,
  // then try deriving from commission/rate (last resort).
  const grossDisplay = b.gross_total || b.gross
    || (b.commission && b.commission_rate
      ? Number(b.commission) / Number(b.commission_rate)
      : 0);
  // iter-300 — display-SSOT fix (was iter-230bs derivation, which drifted
  // e.g. 0.56% instead of 0.50% because it back-computed the % from
  // tds/tcs ÷ an APPROXIMATED tax base, and that approximation didn't
  // always match the engine's true tax_base (P + shipping_base +
  // shipping_gst — see payout_engine.py). Backend now echoes the EXACT
  // fraction it applied (`tds_rate`/`tcs_rate`) — read that directly.
  // Only fall back to the engine's hardcoded defaults for legacy
  // snapshots pre-dating this field (never re-derive from amounts).
  const tdsPct = (b.tds_rate != null ? Number(b.tds_rate) * 100 : 0.10).toFixed(2);
  const tcsPct = (b.tcs_rate != null ? Number(b.tcs_rate) * 100 : 0.50).toFixed(2);
  const pgFeePct = grossDisplay > 0 ? (pgFee / grossDisplay * 100).toFixed(2) : '2.00';
  const payoutFeePct = grossDisplay > 0 ? (Number(b.payout_fee || 0) / grossDisplay * 100).toFixed(2) : '0.25';

  const lines = [
    {
      label: 'Total Amount Received (Gross)', value: grossDisplay,
      sign: '+', tone: 'text-gray-900',
      sub: isSelfShipping
        ? 'Product + Customer-paid shipping (self-managed) + Shipping GST'
        : `Product + Platform shipping + 18% Shipping GST${b.surcharge > 0 ? ' + Surcharge' : ''}`,
      source: txn.parent_order_id
        ? `Parent order ${txn.parent_order_id}.total + this logistics-child order total`
        : 'orders.total_amount (incl. GST) + sibling aniyor_logistics_child if any',
    },
    {
      label: `Commission (${commPctDisplay}%)`, value: b.commission,
      sign: '−', tone: 'text-red-700',
      sub: commSourceLabel,
      source: `commission = gross × ${commPctDisplay}% | authority: ${commSource || 'subscription plan'}`,
    },
    {
      label: 'GST on Commission (18%)', value: b.commission_gst,
      sign: '−', tone: 'text-red-700',
      sub: 'CGST+SGST or IGST on the commission line',
      source: 'commission_gst = commission × 18%',
    },
    {
      label: 'Shipping Pool Contribution', value: sellerPool,
      sign: '−', tone: 'text-red-700',
      sub: isSelfShipping
        ? 'Not deducted — vendor self-ships (zero platform retention)'
        : `5% × Product (capped at Zone-E cap ${fmt(b.seller_pool_cap)})`,
      source: 'seller_pool = min(5% × product_price, Zone-E max cap)',
      hide: isSelfShipping || sellerPool === 0,
    },
    {
      // iter-376 — was previously folded silently into `logistics_deduction`
      // with no line of its own, so the 8 visible lines never summed to
      // the displayed Net for any order with a real Aniyor-managed
      // shipping_base — this line closes that transparency gap.
      label: 'Shipping Amount (retained by Aniyor)', value: Number(b.shipping_base) || 0,
      sign: '−', tone: 'text-red-700',
      sub: 'Platform-managed shipping is invoiced/settled separately — not paid out to the vendor',
      source: 'Deducted in full as part of logistics_deduction (aniyor-managed mode)',
      hide: isSelfShipping || (Number(b.shipping_base) || 0) === 0,
    },
    {
      label: 'Shipping GST (18% on Shipping)', value: b.shipping_gst,
      sign: isSelfShipping ? '+' : '−', tone: isSelfShipping ? 'text-emerald-700' : 'text-red-700',
      sub: isSelfShipping
        ? 'Pass-through to vendor — customer-paid GST flows back unchanged'
        : 'Platform retains the GST collected on shipping',
      source: 'shipping_gst = shipping_base × 18%',
      hide: (Number(b.shipping_base) || Number(b.shipping) || 0) === 0,
    },
    {
      label: `TCS (${tcsPct}%)`,
      value: b.tcs, sign: '−', tone: 'text-red-700',
      sub: 'GST u/s 52 — collected at source on gross',
      source: `tcs = gross × ${tcsPct}% (from Tax Rules tab)`,
    },
    {
      label: `TDS (${tdsPct}%)`,
      value: b.tds, sign: '−', tone: 'text-red-700',
      sub: 'Income Tax u/s 194-O — deducted on gross',
      source: `tds = gross × ${tdsPct}% (from Tax Rules tab)`,
    },
    {
      label: `Payment Gateway Fee (${pgFeePct}% + 18% GST)`, value: pgTotal,
      sign: '−', tone: 'text-red-700',
      sub: `Razorpay charge ${fmt(pgFee)} + GST ${fmt(pgGst)}`,
      source: 'PG already deducted by Razorpay at payment time. pg_fee = gross × 2%, pg_gst = pg_fee × 18%',
    },
    {
      label: `Razorpay Payout Fee (${payoutFeePct}%)`, value: b.payout_fee,
      sign: '−', tone: 'text-red-700',
      sub: 'Per-transfer fee charged by Razorpay Route',
      source: `payout_fee = gross × ${payoutFeePct}% (Razorpay Routes per-transfer fee)`,
    },
  ];

  return (
    <div className="space-y-3" data-testid={`txn-breakdown-${txn.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <Layers className="h-4 w-4 text-indigo-600" />
        <span className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
          Financial SSOT Breakdown — 10 lines
        </span>
        <Badge
          variant="outline"
          className={`text-[10px] ml-2 ${isSelfShipping ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-blue-50 border-blue-300 text-blue-800'}`}
        >
          {isSelfShipping ? 'Self-managed shipping' : 'Aniyor-managed shipping'}
        </Badge>
        <Badge variant="outline" className="text-[10px] ml-auto">
          Source: <code className="ml-1">compute_payout_breakdown</code>
        </Badge>
      </div>
      <div className="bg-white rounded-lg border divide-y">
        {lines.filter((l) => !l.hide).map((l, i) => (
          <TooltipProvider key={i} delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="px-4 py-2.5 flex items-start justify-between hover:bg-gray-50 cursor-help">
                  <div>
                    <p className="text-sm font-medium">{l.label}</p>
                    {l.sub && <p className="text-[11px] text-gray-500 mt-0.5">{l.sub}</p>}
                  </div>
                  <span className={`text-sm font-mono ${l.tone}`}>
                    {l.sign === '+' ? '+ ' : '− '}
                    {fmt(l.value)}
                  </span>
                </div>
              </TooltipTrigger>
              {l.source && (
                <TooltipContent side="left" className="max-w-xs text-[11px]">
                  <p className="font-semibold mb-0.5">How this is calculated:</p>
                  <p className="text-gray-200 font-mono">{l.source}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ))}
        {b.surcharge > 0 && (
          <div className="px-4 py-2.5 flex items-start justify-between bg-amber-50">
            <div>
              <p className="text-sm font-medium text-amber-900">Carrier Surcharge (audit)</p>
              <p className="text-[11px] text-amber-700 mt-0.5">
                Waterfall remainder — actual carrier bill exceeded shipping + pools
              </p>
            </div>
            <span className="text-sm font-mono text-amber-800">− {fmt(b.surcharge)}</span>
          </div>
        )}
        <div className="px-4 py-3 flex items-center justify-between bg-green-50">
          <div>
            <p className="text-sm font-semibold text-green-900">Net Vendor Payout</p>
            <p className="text-[11px] text-green-700">
              Settlement figure transferred to Razorpay Linked Account
            </p>
          </div>
          <span className="text-base font-bold font-mono text-green-800">
            {fmt(b.net_payout)}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
        {txn.batch_id && (
          <span>
            <strong>Batch:</strong>{' '}
            <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">{txn.batch_id}</code>
          </span>
        )}
        {txn.razorpay_transfer_id && (
          <span className="inline-flex items-center gap-1">
            <strong>Razorpay Transfer:</strong>{' '}
            <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">{txn.razorpay_transfer_id}</code>
            {/* iter-232 Phase 10 — inline hold/release controls */}
            <TransferHoldControls
              transferId={txn.razorpay_transfer_id}
              currentlyOnHold={Boolean(txn.on_hold || txn.settlement_status === 'on_hold')}
            />
          </span>
        )}
        <span><strong>Order:</strong> <code>{txn.order_id}</code></span>
      </div>
    </div>
  );
}

// iter-319 — one row of the Admin "Payout Corrections" table. Lets an
// admin tag a decision (pending / recovered / supplementary_paid /
// written_off) + free-text notes. This is a MANUAL status tag only —
// it never triggers any money movement (founder-confirmed scope).
const CORRECTION_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'recovered', label: 'Recovered' },
  { value: 'supplementary_paid', label: 'Supplementary Paid' },
  { value: 'written_off', label: 'Written Off' },
];

const CorrectionRow = ({ correction: c, onSetStatus }) => {
  const [status, setStatus] = useState(c.status || 'pending');
  const [notes, setNotes] = useState(c.notes || '');
  const [saving, setSaving] = useState(false);
  const dirty = status !== (c.status || 'pending') || notes !== (c.notes || '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSetStatus(c.vendor_payout_id, status, notes);
    } finally {
      setSaving(false);
    }
  };

  const diffTone = c.difference >= 0 ? 'text-red-600' : 'text-green-600'; // vendor underpaid vs overpaid
  return (
    <tr className="border-b hover:bg-gray-50" data-testid={`correction-row-${c.vendor_payout_id}`}>
      <td className="py-3 px-3 font-medium text-gray-900">#{(c.order_number || c.order_id?.slice(0, 8) || '').toString().replace(/^#/, '')}</td>
      <td className="py-3 px-3 text-gray-600">{c.vendor_name}</td>
      <td className="py-3 px-3 text-right font-mono">₹{(c.original_net_payout || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      <td className="py-3 px-3 text-right font-mono">₹{(c.corrected_net_payout || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      <td className={`py-3 px-3 text-right font-mono font-semibold ${diffTone}`}>
        {c.difference >= 0 ? '+' : '−'}₹{Math.abs(c.difference || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
      </td>
      <td className="py-3 px-3 text-xs text-gray-500 max-w-[160px] truncate" title={c.reason}>{c.reason}</td>
      <td className="py-3 px-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-[160px] text-xs" data-testid={`correction-status-select-${c.vendor_payout_id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CORRECTION_STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-3 px-3">
        <div className="flex items-center gap-2">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add a note…"
            className="h-8 min-h-8 text-xs w-[180px] resize-none py-1.5"
            data-testid={`correction-notes-${c.vendor_payout_id}`}
          />
          {dirty && (
            <Button
              size="sm" className="h-8 px-2 text-xs"
              onClick={handleSave} disabled={saving}
              data-testid={`correction-save-btn-${c.vendor_payout_id}`}
            >
              {saving ? '…' : 'Save'}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};


const AdminPayouts = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Vendor payouts list
  const [vendorPayouts, setVendorPayouts] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorTransactions, setVendorTransactions] = useState([]);

  // All transactions
  const [allTransactions, setAllTransactions] = useState([]);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // iter-230ay — live payout matrix settings + by-vendor-daily + detailed txns
  const [payoutRowSettings, dispatchPayoutSettings] = useReducer(
    payoutSettingsReducer, undefined, initialPayoutSettings,
  );
  const [vendorDaily, setVendorDaily] = useState([]);
  const [transactionsDetailed, setTransactionsDetailed] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  // Save-Changes confirmation modal
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  // Expanded transaction row id (drawer in All Transactions tab)
  const [expandedTxnId, setExpandedTxnId] = useState(null);
  // Selected vendor-daily row (for "view txns in this batch" deep-link)
  const [filterByBatchId, setFilterByBatchId] = useState(null);

  // Legacy single-payout-settings shape kept for backward compat with
  // existing backend; will be retired in Phase A.
  const [payoutSettings, setPayoutSettings] = useState({
    deferral_start_event: 'order_delivered',
    minimum_hold_days: 7,
    auto_release_enabled: true,
    default_commission_rate: 18, // iter-230ax — bumped 15→18 per spec
    minimum_payout_amount: 1000,
    payout_schedule: 'monthly',
  });

  // Stats
  const [stats, setStats] = useState({
    total_pending: 0,
    total_processing: 0,
    total_completed: 0,
    total_amount_pending: 0,
    total_amount_paid: 0,
    vendors_with_pending: 0,
  });

  // iter-319 — Payout Corrections (Step 1/2 admin decision workflow)
  const [corrections, setCorrections] = useState([]);
  const [correctionsSummary, setCorrectionsSummary] = useState({ total: 0, pending: 0, total_variance: 0 });
  const [correctionsLoading, setCorrectionsLoading] = useState(false);
  const [scanningCorrections, setScanningCorrections] = useState(false);

  useEffect(() => {
    fetchPayoutStats();
    fetchVendorPayouts();
    fetchAllTransactions();
    fetchPayoutSettings();
    fetchPayoutMatrix();
    fetchVendorDaily();
    fetchTransactionsDetailed();
    fetchCorrections();
  }, []);

  const fetchCorrections = async () => {
    setCorrectionsLoading(true);
    try {
      const res = await apiCall('/api/admin/payouts/corrections');
      setCorrections(res.corrections || []);
      setCorrectionsSummary({ total: res.total || 0, pending: res.pending || 0, total_variance: res.total_variance || 0 });
    } catch (e) {
      toast({ title: 'Failed to load corrections', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setCorrectionsLoading(false);
    }
  };

  const handleRunCorrectionScan = async () => {
    setScanningCorrections(true);
    try {
      const res = await apiCall('/api/admin/payouts/corrections/scan', { method: 'POST' });
      toast({
        title: 'Correction scan complete',
        description: `Scanned ${res.scanned} payout(s), ${res.newly_flagged} newly flagged, ${res.notified} vendor(s) notified.`,
      });
      await fetchCorrections();
    } catch (e) {
      toast({ title: 'Scan failed', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setScanningCorrections(false);
    }
  };

  const handleSetCorrectionStatus = async (vendorPayoutId, status, notes) => {
    try {
      await apiCall(`/api/admin/payouts/corrections/${vendorPayoutId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status, notes }),
      });
      toast({ title: 'Correction status updated' });
      await fetchCorrections();
    } catch (e) {
      toast({ title: 'Failed to update status', description: e?.message || String(e), variant: 'destructive' });
    }
  };

  const fetchPayoutStats = async () => {
    try {
      // iter-230bq — switched to /stats-v2 which reads from the SAME
      // unified loader as transactions-detailed. The legacy /stats
      // endpoint was missing BACKFILLED_HISTORICAL + PROJECTED → Stats
      // Cards showed 0 in prod despite the ledger having rows.
      const data = await apiCall('/api/admin/payouts/stats-v2');
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch payout stats:', error);
    }
  };

  const fetchVendorPayouts = async () => {
    setLoading(true);
    try {
      const data = await apiCall('/api/admin/payouts/by-vendor');
      setVendorPayouts(data || []);
    } catch (error) {
      console.error('Failed to fetch vendor payouts:', error);
      setVendorPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTransactions = async () => {
    try {
      // iter-230bq — switched to /transactions-unified which reads
      // from the SAME unified loader as Stats Cards + the All
      // Transactions ledger. The legacy /transactions endpoint only
      // read `db.payout_transactions` and missed all shipment-derived
      // rows (the production canonical writer) → orphan-vendor banner
      // was incorrect.
      const data = await apiCall('/api/admin/payouts/transactions-unified');
      setAllTransactions(data || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setAllTransactions([]);
    }
  };

  const fetchPayoutSettings = async () => {
    try {
      const data = await apiCall('/api/admin/payouts/settings');
      if (data) {
        setPayoutSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch payout settings:', error);
    }
  };

  // iter-230ay — 3-row matrix
  const fetchPayoutMatrix = async () => {
    try {
      const data = await apiCall('/api/admin/payouts/payout-settings');
      if (data) {
        dispatchPayoutSettings({
          type: 'INIT_FROM_SERVER',
          payload: {
            products_aniyor: data.products_aniyor,
            products_self: data.products_self,
            services_rentals: data.services_rentals,
          },
        });
      }
    } catch (error) {
      console.error('Failed to fetch payout matrix:', error);
    }
  };

  const fetchVendorDaily = async () => {
    try {
      const data = await apiCall('/api/admin/payouts/by-vendor-daily');
      setVendorDaily(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch by-vendor-daily:', error);
      setVendorDaily([]);
    }
  };

  const fetchTransactionsDetailed = async () => {
    try {
      const data = await apiCall('/api/admin/payouts/transactions-detailed?limit=500');
      setTransactionsDetailed(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch detailed transactions:', error);
      setTransactionsDetailed([]);
    }
  };

  const handleSavePayoutMatrix = async () => {
    setSavingSettings(true);
    try {
      await apiCall('/api/admin/payouts/payout-settings', {
        method: 'PUT',
        body: JSON.stringify({
          products_aniyor: payoutRowSettings.products_aniyor,
          products_self: payoutRowSettings.products_self,
          services_rentals: payoutRowSettings.services_rentals,
          confirmation_fallback_hours: 72,
          default_commission_fallback_pct: 18.0,
        }),
      });
      toast({ title: 'Saved', description: 'Payout matrix updated successfully.' });
      setShowSaveConfirm(false);
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error?.message || 'Could not persist payout settings.',
        variant: 'destructive',
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchVendorTransactions = async (vendorId) => {
    try {
      const data = await apiCall(`/api/admin/payouts/vendor/${vendorId}/transactions`);
      setVendorTransactions(data || []);
    } catch (error) {
      console.error('Failed to fetch vendor transactions:', error);
      setVendorTransactions([]);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await apiCall('/api/admin/payouts/settings', {
        method: 'PUT',
        body: JSON.stringify(payoutSettings)
      });
      toast({ title: 'Success', description: 'Payout settings saved successfully' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to save settings',
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayout = async (vendorId) => {
    try {
      await apiCall(`/api/admin/payouts/process/${vendorId}`, { method: 'POST' });
      toast({ title: 'Success', description: 'Payout processing initiated' });
      fetchVendorPayouts();
      fetchPayoutStats();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to process payout',
        variant: 'destructive' 
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', class: 'bg-yellow-100 text-yellow-800', icon: Clock },
      processing: { label: 'Held', class: 'bg-blue-100 text-blue-800', icon: RefreshCw },
      completed: { label: 'Completed', class: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      failed: { label: 'Failed', class: 'bg-red-100 text-red-800', icon: XCircle },
      reversed: { label: 'Reversed', class: 'bg-gray-100 text-gray-700', icon: RefreshCw },
      // iter-230bp — projected payouts (paid but not delivered yet)
      projected: { label: 'Projected', class: 'bg-purple-100 text-purple-800', icon: Clock },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={config.class}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // iter-230bp — Admin manual mark-delivered action for stuck/un-fetched
  // courier statuses. POSTs to the same endpoint that auto-computes the
  // SSOT payout and flips status PROJECTED → HELD.
  const handleMarkDelivered = async (shipmentId, vendorName, orderId) => {
    const reason = window.prompt(
      `Mark shipment for ${vendorName} (order ${orderId}) as DELIVERED?\n\n` +
      `This overrides the courier status, sets delivered_at to now, and\n` +
      `flips the payout to HELD (releasable after 7-day reserve).\n\n` +
      `Reason (optional, but recommended for audit):`,
      ''
    );
    if (reason === null) return;  // user cancelled
    try {
      const res = await apiCall(
        `/api/admin/payouts/shipments/${shipmentId}/mark-delivered`,
        {
          method: 'POST',
          body: JSON.stringify({ reason: reason || '' }),
        }
      );
      if (res?.ok) {
        toast({
          title: res.no_op ? 'No change needed' : 'Marked delivered',
          description: res.message || 'Shipment updated.',
        });
        // iter-230bq — refresh both ledger sources so the row flips state.
        await Promise.all([fetchTransactionsDetailed(), fetchVendorDaily()]);
      } else {
        throw new Error(res?.message || 'Mark delivered failed');
      }
    } catch (e) {
      toast({
        title: 'Mark delivered failed',
        description: e?.message || String(e),
        variant: 'destructive',
      });
    }
  };

  // iter-230br — Mark a payout as already settled outside auto-split
  // (Razorpay-dashboard manual transfer OR external bank transfer).
  const handleMarkPayoutProcessed = async (shipmentId, method, txn) => {
    const orderRef = txn.order_number ? `#${txn.order_number}` : (txn.order_id || '').slice(0, 8);
    const isRzp = method === 'razorpay_manual';
    const refLabel = isRzp ? 'Razorpay transfer ID (trf_xxx…)' : 'External reference (NEFT/IMPS/UPI/cheque number)';
    const ref = window.prompt(
      `Mark payout for ${txn.vendor_name} (order ${orderRef}) as ALREADY PROCESSED?\n\n` +
      `Method: ${isRzp ? 'Razorpay Routes manual transfer' : 'External bank transfer'}\n` +
      `Status will flip to RELEASED. Future auto-payout will NOT re-trigger.\n\n` +
      `Enter ${refLabel}:`,
      ''
    );
    if (ref === null) return;
    if (!ref.trim()) {
      toast({
        title: 'Reference required',
        description: `Please provide a ${refLabel} for audit.`,
        variant: 'destructive',
      });
      return;
    }
    const reason = window.prompt(
      'Optional audit note (e.g. "Manually transferred via Razorpay dashboard on 1 Jun"):',
      ''
    ) || '';
    try {
      const body = isRzp
        ? { method, razorpay_transfer_id: ref.trim(), reason }
        : { method, external_reference: ref.trim(), reason };
      const res = await apiCall(
        `/api/admin/payouts/shipments/${shipmentId}/mark-payout-processed`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      if (res?.ok) {
        toast({
          title: res.no_op ? 'No change needed' : 'Payout marked processed',
          description: res.message || 'Shipment updated.',
        });
        await Promise.all([
          fetchTransactionsDetailed(),
          fetchVendorDaily(),
          fetchPayoutStats(),
          fetchAllTransactions(),
        ]);
      } else {
        throw new Error(res?.message || 'Mark payout processed failed');
      }
    } catch (e) {
      toast({
        title: 'Mark payout processed failed',
        description: e?.message || String(e),
        variant: 'destructive',
      });
    }
  };

  // iter-307 — Re-attempt a FAILED payout transfer. Reuses the existing
  // admin `trigger-transfer` endpoint (already idempotent — it only
  // no-ops if `payout_transfer_id` is already set, which a FAILED
  // shipment never has). Recomputes the escrow hold from
  // delivered_at + return-window days (NOT force_immediate) so the
  // vendor gets the standard hold period, unless that window has
  // already elapsed — in which case the backend self-heals by
  // clamping the hold to release almost immediately.
  const handleReattemptPayout = async (txn) => {
    const orderRef = txn.order_number ? `#${txn.order_number}` : (txn.order_id || '').slice(0, 8);
    if (!window.confirm(
      `Re-attempt payout for ${txn.vendor_name} (order ${orderRef})?\n\n` +
      `This creates a NEW Razorpay transfer, held for the standard return-\n` +
      `window period from the delivery date (or released almost immediately\n` +
      `if that window has already passed).`
    )) return;
    try {
      const res = await apiCall(`/api/orders/${txn.order_id}/trigger-transfer`, {
        method: 'POST',
        body: JSON.stringify({ force_immediate: false, note: 'Re-attempt from Admin Payouts (previously failed)' }),
      });
      if (res?.status === 'processing') {
        toast({
          title: 'Retry in progress',
          description: res.message || 'Transfer is processing in the background. Refresh in ~30s.',
        });
      } else {
        toast({
          title: 'Payout re-attempt succeeded',
          description: `New transfer created: ${res?.transfer_id || res?.result?.transfer_id || 'success'}`,
        });
      }
      await Promise.all([fetchTransactionsDetailed(), fetchVendorDaily(), fetchPayoutStats()]);
    } catch (e) {
      toast({
        title: 'Re-attempt failed',
        description: e?.message || String(e),
        variant: 'destructive',
      });
      await fetchTransactionsDetailed();
    }
  };

  // iter-316 Phase 2 — "Sync with Razorpay" reconciliation actions.
  const [syncingTxnId, setSyncingTxnId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const handleSyncRazorpay = async (txn) => {
    if (!txn.shipment_id) {
      toast({ title: 'Cannot sync', description: 'No shipment record for this row.', variant: 'destructive' });
      return;
    }
    setSyncingTxnId(txn.id);
    try {
      const res = await apiCall(`/api/admin/payouts/shipments/${txn.shipment_id}/sync-razorpay`, { method: 'POST' });
      if (res.changed) {
        toast({
          title: 'Payout status updated',
          description: `${res.old_status} → ${res.new_status} (synced from Razorpay)`,
        });
      } else {
        toast({ title: 'Already up to date', description: `Razorpay confirms: ${res.razorpay_status}` });
      }
      await Promise.all([fetchTransactionsDetailed(), fetchVendorDaily(), fetchPayoutStats()]);
    } catch (e) {
      toast({ title: 'Sync failed', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setSyncingTxnId(null);
    }
  };

  const handleSyncAllRazorpay = async () => {
    setSyncingAll(true);
    try {
      const res = await apiCall('/api/admin/payouts/sync-razorpay-bulk?limit=100', { method: 'POST' });
      toast({
        title: 'Razorpay sync complete',
        description: `Checked ${res.checked} payout(s), updated ${res.updated}${res.errors?.length ? `, ${res.errors.length} error(s)` : ''}.`,
      });
      await Promise.all([fetchTransactionsDetailed(), fetchVendorDaily(), fetchPayoutStats()]);
    } catch (e) {
      toast({ title: 'Bulk sync failed', description: e?.message || String(e), variant: 'destructive' });
    } finally {
      setSyncingAll(false);
    }
  };

  // iter-316 Phase 3 — Payout Receipt preview/download (auth-safe fetch +
  // blob URL, mirroring the Commission Invoice preview pattern).
  const openPayoutReceipt = async (txn, download = false) => {
    if (!txn.shipment_id) return;
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const path = download ? 'pdf' : 'html';
      const response = await fetch(`${BACKEND}/api/payouts/${txn.shipment_id}/receipt/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `payout-receipt-${txn.order_number || txn.shipment_id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast({ title: 'Receipt unavailable', description: e?.message || String(e), variant: 'destructive' });
    }
  };


  const filteredTransactions = allTransactions.filter(t => {
    if (transactionFilter !== 'all' && t.status !== transactionFilter) return false;
    if (searchQuery && !t.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !t.order_id.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Vendor Payouts</h1>
            <p className="text-gray-500 mt-1">Manage vendor payouts, view transactions, and configure settings</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleSyncAllRazorpay}
              disabled={syncingAll}
              data-testid="sync-all-razorpay-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncingAll ? 'animate-spin' : ''}`} />
              {syncingAll ? 'Syncing…' : 'Sync with Razorpay'}
            </Button>
            <Button variant="outline" onClick={() => {
              fetchPayoutStats();
              fetchVendorPayouts();
              fetchAllTransactions();
              fetchPayoutMatrix();
              fetchVendorDaily();
              fetchTransactionsDetailed();
            }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Clock className="h-8 w-8 mx-auto text-yellow-500 mb-2" />
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold">{stats.total_pending}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                <p className="text-sm text-gray-600">Processing</p>
                <p className="text-2xl font-bold">{stats.total_processing}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle2 className="h-8 w-8 mx-auto text-green-500 mb-2" />
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold">{stats.total_completed}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <ArrowUpRight className="h-8 w-8 mx-auto text-orange-500 mb-2" />
                <p className="text-sm text-gray-600">Pending Amount</p>
                <p className="text-2xl font-bold">₹{(stats.total_amount_pending / 1000).toFixed(0)}K</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <ArrowDownRight className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
                <p className="text-sm text-gray-600">Total Paid</p>
                <p className="text-2xl font-bold">₹{(stats.total_amount_paid / 100000).toFixed(1)}L</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Users className="h-8 w-8 mx-auto text-purple-500 mb-2" />
                <p className="text-sm text-gray-600">Vendors Pending</p>
                <p className="text-2xl font-bold">{stats.vendors_with_pending}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* iter-230ar — Orphan detection banner. If ≥80 % of transactions
            have a vendor_name of "Unknown" (vendor_id doesn't resolve in
            db.users), surface a cleanup CTA pointing at the Maintenance
            Runner template. Background: a one-shot dev seed inserted
            ~20 dummy rows in production before the ALLOW_PAYOUT_SEED
            guard landed; the vendors who originally owned those rows
            have since been migrated. */}
        {allTransactions.length > 0 && (
          (() => {
            const orphans = allTransactions.filter(
              (t) => !t.vendor_name || t.vendor_name === 'Unknown'
            ).length;
            const ratio = orphans / allTransactions.length;
            if (ratio < 0.8) return null;
            return (
              <Card
                className="border-amber-300 bg-amber-50"
                data-testid="payout-orphan-banner"
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-amber-900">
                        {orphans} of {allTransactions.length} transactions are orphaned (vendor no longer exists)
                      </p>
                      <p className="text-sm text-amber-800 mt-1">
                        These rows came from a one-shot dev seed before the
                        production guard landed. Run the{' '}
                        <code className="px-1 py-0.5 bg-amber-100 rounded text-xs">
                          cleanup-dummy-payout-transactions
                        </code>{' '}
                        template from the Maintenance Runner to wipe them. Real
                        delivery-driven payouts will start flowing in automatically
                        once shipments transition to <code className="px-1 bg-amber-100 rounded text-xs">delivered</code>.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 border-amber-400 text-amber-900 hover:bg-amber-100"
                        onClick={() => window.location.assign('/admin/maintenance-runner')}
                        data-testid="payout-orphan-banner-cta"
                      >
                        Open Maintenance Runner →
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="overview" data-testid="payouts-tab-by-vendor">
                <Users className="h-4 w-4 mr-2" />
                By Vendor
              </TabsTrigger>
              <TabsTrigger value="transactions" data-testid="payouts-tab-all-txn">
                <DollarSign className="h-4 w-4 mr-2" />
                All Transactions
              </TabsTrigger>
              <TabsTrigger value="wallets" data-testid="payouts-tab-wallets">
                <Wallet className="h-4 w-4 mr-2" />
                Shipping Pool Wallets
              </TabsTrigger>
              <TabsTrigger value="settings" data-testid="payouts-tab-settings">
                <Settings className="h-4 w-4 mr-2" />
                Payout Settings
              </TabsTrigger>
              <TabsTrigger value="corrections" data-testid="payouts-tab-corrections">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Corrections
              </TabsTrigger>
            </TabsList>

            {/* iter-230ay — Export Report dropdown (live streaming endpoint) */}
            <ExportReportMenu
              apiCall={apiCall}
              currentFilters={{ status: transactionFilter, search: searchQuery }}
            />
          </div>

          {/* ─── By Vendor — Daily Layout (MOCK iter-230ax) ─────────────── */}
          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>Daily Payout Summary by Vendor</CardTitle>
                    <CardDescription>
                      One row per vendor per day. Multiple transactions for the same
                      vendor maturing on the same day are batched into a single
                      Razorpay transfer — the shared{' '}
                      <code className="px-1 bg-gray-100 rounded text-xs">Batch / Transfer ID</code>{' '}
                      is shown below.
                    </CardDescription>
                  </div>
                  {filterByBatchId && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setFilterByBatchId(null)}
                      data-testid="clear-batch-filter-btn"
                    >
                      ← Clear filter ({filterByBatchId})
                    </Button>
                  )}
                </div>
                <Badge variant="outline" className="mt-2 w-fit bg-green-50 border-green-300 text-green-800">
                  LIVE — IST daily aggregation
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Vendor</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Txns</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Gross</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Net Payout</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Batch / Transfer ID</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendorDaily.length === 0 && (
                        <tr>
                          <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                            No daily payout aggregations yet. Rows will appear here once
                            shipments transition to <code className="px-1 bg-gray-100 rounded text-xs">delivered</code>{' '}
                            and payout transactions are generated.
                          </td>
                        </tr>
                      )}
                      {vendorDaily.map((row) => (
                        <tr
                          key={row.id}
                          className="border-t hover:bg-gray-50"
                          data-testid={`vendor-daily-row-${row.id}`}
                        >
                          <td className="px-4 py-3 text-sm font-medium">
                            {new Date(row.date).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                            })}
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-sm">{row.vendor_name}</p>
                            <p className="text-xs text-gray-500">{row.vendor_email}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-right">{row.transaction_count}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            ₹{row.gross_amount.toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-green-700">
                            ₹{row.net_amount.toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-0.5">
                              <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">
                                {row.batch_id}
                              </code>
                              {row.razorpay_transfer_id && (
                                <code className="text-[10px] text-gray-500 font-mono">
                                  {row.razorpay_transfer_id}
                                </code>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">{getStatusBadge(row.status)}</td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-8 w-8 p-0"
                                  data-testid={`by-vendor-actions-${row.id}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem
                                  onClick={() => {
                                    setFilterByBatchId(row.batch_id);
                                    setActiveTab('transactions');
                                  }}
                                  data-testid={`view-batch-txns-${row.id}`}
                                >
                                  <Eye className="h-3.5 w-3.5 mr-2" />
                                  View transactions
                                </DropdownMenuItem>
                                {row.single_shipment_id ? (
                                  <DropdownMenuItem
                                    onClick={() => handleMarkDelivered(
                                      row.single_shipment_id, row.vendor_name, row.batch_id,
                                    )}
                                    data-testid={`by-vendor-mark-delivered-${row.id}`}
                                    className="text-emerald-700 focus:text-emerald-800"
                                  >
                                    <CheckSquare className="h-3.5 w-3.5 mr-2" />
                                    Mark delivered
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    disabled
                                    data-testid={`by-vendor-mark-delivered-disabled-${row.id}`}
                                    className="text-gray-400 cursor-not-allowed"
                                    title={
                                      row.transaction_count > 1
                                        ? 'Multiple txns — open All Transactions to mark individually'
                                        : 'No shipment to mark — already delivered or projected without shipment'
                                    }
                                  >
                                    <CheckSquare className="h-3.5 w-3.5 mr-2" />
                                    Mark delivered
                                    <span className="ml-auto text-[10px]">
                                      {row.transaction_count > 1 ? `${row.transaction_count} txns` : 'n/a'}
                                    </span>
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── All Transactions — Granular Ledger w/ Drawer (MOCK iter-230ax) ─ */}
          <TabsContent value="transactions" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>All Payout Transactions</CardTitle>
                    <CardDescription>
                      Line item per transaction. Click any row to expand the{' '}
                      <strong>Financial SSOT breakdown</strong> (Gross → Commission →
                      Taxes → Shipping Pool → Net).
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search vendor / order / batch..."
                        className="pl-10 w-64"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Select value={transactionFilter} onValueChange={setTransactionFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Badge variant="outline" className="mt-2 w-fit bg-green-50 border-green-300 text-green-800">
                  LIVE — 10-line SSOT breakdown per row
                </Badge>
                {filterByBatchId && (
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <Filter className="h-4 w-4 text-blue-600" />
                    <span>
                      Filtered to batch <code className="bg-gray-100 px-1 rounded">{filterByBatchId}</code>
                    </span>
                    <Button variant="link" size="sm" className="h-auto p-0"
                      onClick={() => setFilterByBatchId(null)}>
                      Clear
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 w-8"></th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Vendor</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Order ID</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Gross</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Net Payout</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Batch / Transfer ID</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionsDetailed.length === 0 && (
                        <tr>
                          <td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-500">
                            No payout transactions match the current filters.
                          </td>
                        </tr>
                      )}
                      {transactionsDetailed
                        .filter((t) => !filterByBatchId || t.batch_id === filterByBatchId)
                        .filter((t) => transactionFilter === 'all' || t.status === transactionFilter)
                        .filter((t) => !searchQuery
                          || (t.vendor_name || '').toLowerCase().includes(searchQuery.toLowerCase())
                          || (t.order_id || '').toLowerCase().includes(searchQuery.toLowerCase())
                          || (String(t.order_number || '')).toLowerCase().includes(searchQuery.toLowerCase())
                          || ((t.batch_id || '').toLowerCase().includes(searchQuery.toLowerCase())))
                        .map((txn) => {
                          // Fallback: legacy snapshots use 'gross' instead of 'gross_total'
                          const b = txn.breakdown || {};
                          const grossDisplay = b.gross_total || b.gross || 0;
                          const netDisplay = b.net_payout || 0;
                          return (
                          <React.Fragment key={txn.id}>
                            <tr
                              className="border-t hover:bg-gray-50 cursor-pointer"
                              onClick={() => setExpandedTxnId(expandedTxnId === txn.id ? null : txn.id)}
                              data-testid={`txn-row-${txn.id}`}
                            >
                              <td className="px-4 py-3">
                                {expandedTxnId === txn.id
                                  ? <ChevronDown className="h-4 w-4 text-gray-500" />
                                  : <ChevronRight className="h-4 w-4 text-gray-500" />}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium">{txn.vendor_name}</td>
                              <td className="px-4 py-3 text-sm">
                                {txn.order_number ? (
                                  <TooltipProvider>
                                    <Tooltip delayDuration={150}>
                                      <TooltipTrigger asChild>
                                        <code className="text-xs bg-indigo-50 border border-indigo-200 text-indigo-800 px-2 py-0.5 rounded font-mono">
                                          #{txn.order_number}
                                        </code>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="font-mono text-[10px] max-w-xs break-all">
                                        Internal ID: {txn.order_id}
                                        {txn.parent_order_id && (
                                          <div className="mt-1 text-amber-300">↳ Logistics child of {txn.parent_order_id}</div>
                                        )}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <code className="text-xs text-gray-500 font-mono">{(txn.order_id || '').slice(0, 8)}…</code>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-right">
                                ₹{Number(grossDisplay).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-semibold text-green-700">
                                ₹{Number(netDisplay).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="px-4 py-3">
                                {txn.batch_id ? (
                                  <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">
                                    {txn.batch_id}
                                  </code>
                                ) : txn.razorpay_transfer_id ? (
                                  <code
                                    className="text-[10px] text-gray-500 font-mono"
                                    data-testid={`txn-transfer-id-${txn.id}`}
                                  >
                                    {txn.razorpay_transfer_id}
                                  </code>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {txn.created_at ? new Date(txn.created_at).toLocaleString('en-IN', {
                                  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                                }) : '—'}
                              </td>
                              <td className="px-4 py-3">{getStatusBadge(txn.status)}</td>
                              <td
                                className="px-4 py-3 text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost" size="sm"
                                      className="h-8 w-8 p-0"
                                      data-testid={`txn-actions-${txn.id}`}
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-64">
                                    <DropdownMenuItem
                                      onClick={() => setExpandedTxnId(
                                        expandedTxnId === txn.id ? null : txn.id,
                                      )}
                                      data-testid={`txn-view-breakdown-${txn.id}`}
                                    >
                                      <Eye className="h-3.5 w-3.5 mr-2" />
                                      {expandedTxnId === txn.id ? 'Hide breakdown' : 'View breakdown'}
                                    </DropdownMenuItem>
                                    {(txn.status === 'projected' || txn.status === 'pending') && txn.shipment_id ? (
                                      <DropdownMenuItem
                                        onClick={() => handleMarkDelivered(
                                          txn.shipment_id, txn.vendor_name, txn.order_id,
                                        )}
                                        data-testid={`mark-delivered-btn-${txn.id}`}
                                        className="text-emerald-700 focus:text-emerald-800"
                                      >
                                        <CheckSquare className="h-3.5 w-3.5 mr-2" />
                                        Mark delivered
                                      </DropdownMenuItem>
                                    ) : (
                                      <DropdownMenuItem
                                        disabled
                                        className="text-gray-400 cursor-not-allowed"
                                        title={
                                          !txn.shipment_id
                                            ? 'No shipment id yet — projected from order'
                                            : 'Already delivered / completed'
                                        }
                                      >
                                        <CheckSquare className="h-3.5 w-3.5 mr-2" />
                                        Mark delivered
                                        <span className="ml-auto text-[10px]">
                                          {!txn.shipment_id ? 'no shipment' : 'n/a'}
                                        </span>
                                      </DropdownMenuItem>
                                    )}
                                    {/* iter-230br — Mark Payout Processed sub-menu */}
                                    <DropdownMenuSeparator />
                                    {txn.status !== 'completed' && txn.shipment_id ? (
                                      <DropdownMenuSub>
                                        <DropdownMenuSubTrigger
                                          data-testid={`mark-payout-processed-trigger-${txn.id}`}
                                          className="text-indigo-700 focus:text-indigo-800"
                                        >
                                          <CreditCard className="h-3.5 w-3.5 mr-2" />
                                          Mark payout processed
                                        </DropdownMenuSubTrigger>
                                        <DropdownMenuSubContent className="w-64">
                                          <DropdownMenuLabel className="text-[10px] text-gray-500 px-2 py-1">
                                            Choose settlement method
                                          </DropdownMenuLabel>
                                          <DropdownMenuItem
                                            onClick={() => handleMarkPayoutProcessed(
                                              txn.shipment_id, 'razorpay_manual', txn,
                                            )}
                                            data-testid={`mark-payout-rzp-${txn.id}`}
                                          >
                                            <CheckSquare className="h-3.5 w-3.5 mr-2 text-blue-600" />
                                            Paid via Razorpay Routes
                                            <span className="ml-auto text-[10px] text-gray-400">trf_id</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => handleMarkPayoutProcessed(
                                              txn.shipment_id, 'external', txn,
                                            )}
                                            data-testid={`mark-payout-ext-${txn.id}`}
                                          >
                                            <CheckSquare className="h-3.5 w-3.5 mr-2 text-amber-600" />
                                            Paid externally (NEFT/IMPS/UPI)
                                            <span className="ml-auto text-[10px] text-gray-400">ref#</span>
                                          </DropdownMenuItem>
                                        </DropdownMenuSubContent>
                                      </DropdownMenuSub>
                                    ) : (
                                      <DropdownMenuItem
                                        disabled
                                        className="text-gray-400 cursor-not-allowed"
                                        title={
                                          !txn.shipment_id
                                            ? 'Synthesise on-the-fly projected — no shipment yet'
                                            : 'Already settled — payout cannot be re-marked'
                                        }
                                      >
                                        <CreditCard className="h-3.5 w-3.5 mr-2" />
                                        Mark payout processed
                                        <span className="ml-auto text-[10px]">
                                          {!txn.shipment_id ? 'no shipment' : 'settled'}
                                        </span>
                                      </DropdownMenuItem>
                                    )}
                                    {/* iter-307 — Re-attempt Payout for FAILED transfers */}
                                    {txn.status === 'failed' && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onClick={() => handleReattemptPayout(txn)}
                                          data-testid={`txn-reattempt-payout-${txn.id}`}
                                          className="text-orange-700 focus:text-orange-800"
                                        >
                                          <RefreshCw className="h-3.5 w-3.5 mr-2" />
                                          Re-attempt Payout
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                    {/* iter-316 Phase 2 — Sync with Razorpay (per-row reconciliation) */}
                                    {txn.razorpay_transfer_id && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onClick={() => handleSyncRazorpay(txn)}
                                          disabled={syncingTxnId === txn.id}
                                          data-testid={`txn-sync-razorpay-${txn.id}`}
                                          className="text-blue-700 focus:text-blue-800"
                                        >
                                          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${syncingTxnId === txn.id ? 'animate-spin' : ''}`} />
                                          {syncingTxnId === txn.id ? 'Syncing…' : 'Sync with Razorpay'}
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                    {/* iter-316 Phase 3 — Payout Receipt (completed payouts only) */}
                                    {txn.status === 'completed' && txn.shipment_id && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onClick={() => openPayoutReceipt(txn, false)}
                                          data-testid={`txn-view-receipt-${txn.id}`}
                                        >
                                          <FileText className="h-3.5 w-3.5 mr-2" />
                                          View Receipt
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => openPayoutReceipt(txn, true)}
                                          data-testid={`txn-download-receipt-${txn.id}`}
                                        >
                                          <Download className="h-3.5 w-3.5 mr-2" />
                                          Download Receipt PDF
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                            {expandedTxnId === txn.id && (
                              <tr className="bg-gradient-to-br from-blue-50/50 to-indigo-50/30">
                                <td colSpan={9} className="px-8 py-5">
                                  <TransactionBreakdownDrawer txn={txn} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Wallets — iter-230bv ───────────────────────────────── */}
          <TabsContent value="wallets" className="space-y-6">
            <AdminWalletsTab />
          </TabsContent>

          {/* ─── Payout Settings — 4-row Matrix (MOCK iter-230ax) ─────────── */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>Payout Trigger & Hold Configuration</CardTitle>
                    <CardDescription>
                      Defines when the payout-release countdown starts and how many
                      days funds are held before release per listing vertical.
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="bg-green-50 border-green-300 text-green-800">
                    LIVE — backed by payout_settings_matrix
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {PAYOUT_SETTINGS_ROWS.map((row) => {
                  const Icon = row.icon;
                  const current = payoutRowSettings[row.id];
                  return (
                    <div
                      key={row.id}
                      className="border rounded-lg p-5 bg-white"
                      data-testid={`payout-settings-row-${row.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-2.5 bg-indigo-50 rounded-lg">
                          <Icon className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-12 md:col-span-5">
                            <Label className="text-sm font-semibold">{row.label}</Label>
                            <p className="text-xs text-gray-500 mt-0.5">{row.description}</p>
                          </div>
                          <div className="col-span-6 md:col-span-3">
                            <Label className="text-xs uppercase tracking-wider text-gray-500">
                              Hold Period (N days)
                            </Label>
                            <Input
                              type="number" min="0" max="90"
                              value={current.hold_days}
                              onChange={(e) => dispatchPayoutSettings({
                                type: 'SET_HOLD', row: row.id, value: parseInt(e.target.value, 10) || 0,
                              })}
                              className="mt-1"
                              data-testid={`hold-days-${row.id}`}
                            />
                          </div>
                          <div className="col-span-6 md:col-span-4">
                            <Label className="text-xs uppercase tracking-wider text-gray-500">
                              From
                            </Label>
                            <Select
                              value={current.anchor_from}
                              onValueChange={(v) => dispatchPayoutSettings({
                                type: 'SET_ANCHOR', row: row.id, value: v,
                              })}
                            >
                              <SelectTrigger className="mt-1" data-testid={`anchor-${row.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {row.anchorOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Confirmation Fallback — static display (per user decision) */}
                <div className="border-2 border-dashed rounded-lg p-5 bg-gray-50/50">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 bg-amber-50 rounded-lg">
                      <Clock className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold">
                          Customer Delivery Confirmation Fallback Window
                        </Label>
                        <div className="px-3 py-1 bg-amber-100 border border-amber-300 rounded-md">
                          <span className="font-bold text-amber-900">{CONFIRMATION_FALLBACK_HOURS} Hrs</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                        If a customer fails to manually confirm delivery (via OTP /
                        in-app action) within this window after the courier scan,
                        the order auto-confirms and the N-day payout hold countdown
                        begins. This value is system-wide and fixed.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Save bar */}
                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => dispatchPayoutSettings({ type: 'RESET_DEFAULTS' })}
                    data-testid="payout-settings-reset-btn"
                  >
                    Reset to Defaults
                  </Button>
                  <Button
                    onClick={() => setShowSaveConfirm(true)}
                    data-testid="payout-settings-save-btn"
                  >
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Live preview of current settings (helps QA & finance) */}
            <Card className="border-blue-200 bg-blue-50/40">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Active Configuration Preview</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1.5">
                {PAYOUT_SETTINGS_ROWS.map((row) => {
                  const c = payoutRowSettings[row.id];
                  const anchorLabel = row.anchorOptions.find((o) => o.value === c.anchor_from)?.label;
                  return (
                    <div key={row.id} className="flex justify-between">
                      <span className="text-gray-700">{row.label}</span>
                      <span className="font-medium text-gray-900">
                        {c.hold_days} days from <em>{anchorLabel}</em>
                      </span>
                    </div>
                  );
                })}
                <div className="flex justify-between border-t border-blue-200 pt-1.5 mt-2">
                  <span className="text-gray-700">Confirmation Fallback Window</span>
                  <span className="font-medium text-gray-900">{CONFIRMATION_FALLBACK_HOURS} Hrs (fixed)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Commission (no subscription) fallback</span>
                  <span className="font-medium text-gray-900">18%</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* iter-319 — Payout Corrections: Step 2 admin decision workflow.
              Money movement is NOT automated here — `correction_status` is
              a manual tag the admin sets after handling the actual
              adjustment (bank transfer / netting a future payout) outside
              this system, per founder-confirmed scope. */}
          <TabsContent value="corrections" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    Payout Corrections
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Vendor payouts with a verified financial discrepancy (a recalculated
                    breakdown exists that differs from what was actually paid). Detected
                    dynamically — never a hardcoded list.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={handleRunCorrectionScan}
                  disabled={scanningCorrections}
                  data-testid="run-correction-scan-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${scanningCorrections ? 'animate-spin' : ''}`} />
                  {scanningCorrections ? 'Scanning…' : 'Run Scan Now'}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Flagged</p>
                    <p className="text-2xl font-bold text-gray-900" data-testid="corrections-total">{correctionsSummary.total}</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4">
                    <p className="text-xs text-amber-600 uppercase tracking-wide">Pending Decision</p>
                    <p className="text-2xl font-bold text-amber-700" data-testid="corrections-pending">{correctionsSummary.pending}</p>
                  </div>
                  <div className={`rounded-lg p-4 ${correctionsSummary.total_variance < 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                    <p className={`text-xs uppercase tracking-wide ${correctionsSummary.total_variance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      Net Variance (Vendor Over/Under-paid)
                    </p>
                    <p className={`text-2xl font-bold ${correctionsSummary.total_variance < 0 ? 'text-red-700' : 'text-green-700'}`} data-testid="corrections-total-variance">
                      {correctionsSummary.total_variance >= 0 ? '+' : ''}
                      ₹{Math.abs(correctionsSummary.total_variance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>

                {correctionsLoading ? (
                  <div className="text-center py-12 text-gray-400">Loading corrections…</div>
                ) : corrections.length === 0 ? (
                  <div className="text-center py-12 text-gray-400" data-testid="corrections-empty-state">
                    No correction-eligible payouts found. Run a scan to check for newly detected discrepancies.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wide">
                          <th className="py-2 px-3">Order</th>
                          <th className="py-2 px-3">Vendor</th>
                          <th className="py-2 px-3 text-right">Original Net</th>
                          <th className="py-2 px-3 text-right">Corrected Net</th>
                          <th className="py-2 px-3 text-right">Difference</th>
                          <th className="py-2 px-3">Reason</th>
                          <th className="py-2 px-3">Status</th>
                          <th className="py-2 px-3">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {corrections.map((c) => (
                          <CorrectionRow key={c.vendor_payout_id} correction={c} onSetStatus={handleSetCorrectionStatus} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ─── Save Confirmation Dialog (iter-230ax MOCK) ─────────────────── */}
        <Dialog open={showSaveConfirm} onOpenChange={setShowSaveConfirm}>
          <DialogContent className="max-w-lg" data-testid="payout-settings-save-dialog">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-100 rounded-full">
                  <AlertCircle className="h-5 w-5 text-amber-700" />
                </div>
                <DialogTitle>Confirm Configuration Change</DialogTitle>
              </div>
              <DialogDescription className="pt-3 leading-relaxed">
                Changing these fallback windows and timelines will alter automated
                backend countdowns for <strong>all pending and future order cycles</strong>.
                Already-released payouts are unaffected, but every shipment currently
                in escrow will have its release date <em>recomputed</em> against
                the new anchors and hold periods. This action is logged in the
                admin audit trail.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-gray-50 rounded-lg p-4 mt-2 text-sm space-y-1">
              {PAYOUT_SETTINGS_ROWS.map((row) => {
                const c = payoutRowSettings[row.id];
                const anchorLabel = row.anchorOptions.find((o) => o.value === c.anchor_from)?.label;
                return (
                  <div key={row.id} className="flex justify-between">
                    <span className="text-gray-600">{row.label}</span>
                    <span className="font-medium">{c.hold_days}d · {anchorLabel}</span>
                  </div>
                );
              })}
            </div>
            <DialogFooter className="pt-3">
              <Button variant="outline" onClick={() => setShowSaveConfirm(false)}
                data-testid="payout-settings-save-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleSavePayoutMatrix}
                disabled={savingSettings}
                data-testid="payout-settings-save-confirm"
              >
                {savingSettings ? 'Saving…' : 'Yes, Apply Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminPayouts;
